import React, { useRef } from 'react';
import { 
  CheckCircle, Layers, Upload, Sparkles, 
  RotateCcw, Share2, MessageCircle 
} from 'lucide-react';
import Button from '@/components/ui/Button';
import { motion, useScroll, useTransform } from 'framer-motion';
import FunctionalityCard from '@/components/sections/FunctionalityCard';

const features = [
  {
    icon: <Layers className="w-5 h-5 text-orcsys-500" />,
    title: "Architecture native",
    description: "Applications optimisées pour chaque plateforme"
  },
  {
    icon: <Upload className="w-5 h-5 text-orcsys-500" />,
    title: "Cloud intégré",
    description: "Synchronisation et stockage sécurisé des données"
  },
  {
    icon: <Sparkles className="w-5 h-5 text-orcsys-500" />,
    title: "UI/UX premium",
    description: "Interfaces élégantes et hautement personnalisables"
  },
  {
    icon: <RotateCcw className="w-5 h-5 text-orcsys-500" />,
    title: "Synchronisation",
    description: "Données accessibles sur tous vos appareils"
  },
  {
    icon: <Share2 className="w-5 h-5 text-orcsys-500" />,
    title: "Partage social",
    description: "Intégration avec les réseaux sociaux populaires"
  },
  {
    icon: <MessageCircle className="w-5 h-5 text-orcsys-500" />,
    title: "Notifications push",
    description: "Alertes personnalisées pour engager vos utilisateurs"
  }
];

const TechnologiesSection = () => {
  const technologiesRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: technologiesRef,
    offset: ["start end", "end start"]
  });


  return (
    <section ref={technologiesRef} className="bg-white py-20 lg:py-24 relative overflow-hidden">
      <div className="container px-4 mx-auto relative z-10">
        <div 
          className="max-w-4xl mx-auto"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Des applications <span className="text-orcsys-500">performantes</span> et évolutives
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Nous développons des applications mobiles optimisées pour iOS et Android,
                avec une attention particulière à l'expérience utilisateur et aux performances.
              </p>

              <ul className="space-y-4">
                {[
                  "Développement natif ou cross-platform selon vos besoins",
                  "Interfaces utilisateur intuitives et attrayantes",
                  "Performances optimisées pour une expérience fluide",
                  "Intégration avec les API et services tiers",
                  "Solutions de paiement sécurisées si nécessaire",
                  "Analyse de données utilisateurs pour amélioration continue"
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <CheckCircle className="w-5 h-5 text-orcsys-500 mt-0.5 mr-3 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-8">
                <Button.Link 
                  href="#estimation" 
                  variant="outline"
                  className="border-orcsys-400 text-orcsys-500"
                >
                  Demander une estimation
                </Button.Link>
              </div>
            </div>

            <div>
              <div className="relative p-1 bg-white rounded-2xl shadow-xl border border-slate-200">
                <div className="absolute inset-0 bg-gradient-to-br from-orcsys-50/30 to-transparent rounded-2xl" />
                
                {/* Fixed grid layout for feature cards */}
                <div className="relative grid grid-cols-2 gap-4 p-5">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      className="group"
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <FunctionalityCard 
                        icon={feature.icon} 
                        title={feature.title} 
                        description={feature.description}
                        index={index} 
                      />
                    </motion.div>
                  ))}
                </div>

                {/* Blob backgrounds */}
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-orcsys-500/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-orcsys-500/10 rounded-full blur-3xl" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background elements */}
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-orcsys-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/3 left-0 w-96 h-96 bg-orcsys-500/5 rounded-full blur-3xl" />
    </section>
  );
};

export default TechnologiesSection; 